import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import _debounce from "lodash/debounce";
import {
  Button,
  Checkbox,
  Card,
  Form,
  Grid,
  Icon,
  Input,
  Loader,
  Modal,
  Popup,
  Search,
} from "semantic-ui-react";
import "./nonBOAItems.css";
import {
  apiAddNonBOAProduct,
  apiGetNonBOAProductsList,
} from "../../containers/NonBOAItems/nonBOAItems.api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import NumericInputBox from "../NumericInputBox";
import { formatPrice } from "../../utils/utils";

const NonBOAItemsForm = ({
  loading,
  onAddNonBOAItem,
  onUpdateNonBOAItem,
  onRemoveNonBOAItem,
  inventoryNonBOA,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [nonBOAProductList, setNonBOAProductList] = useState([]);
  const [formData, setFormData] = useState({});
  const [selectedNonBOAProduct, setSelectedNonBOAProduct] = useState({});
  const [selectedNonBOAItemIndex, setSelectedNonBOAItemIndex] = useState(null);
  const [nonBOAProductSaved, setNonBOAProductSaved] = useState(false);
  const [addNonBOAItemOpen, setAddNonBOAItemOpen] = useState(false);
  const [openRemoveNonBOAProduct, setOpenRemoveNonBOAProduct] = useState(false);
  const [hasBarcodeError, setHasBarcodeError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [seachValue, setSearchValue] = useState("");

  useEffect(() => {
    getNonBOAProductsList();
  }, []);

  useEffect(() => {
    getNonBOAProductsList(searchTerm);
  }, [searchTerm]);

  const getNonBOAProductsList = async (keyword = "") => {
    setIsLoading(true);
    // if (keyword === "") {
    //   setIsLoading(false);
    //   setNonBOAProductList([
    //     {
    //       id: null,
    //       barcode: "NONBOA",
    //       description: "",
    //       buyPrice: null,
    //       sellPrice: null,
    //       isDiscountApplied: false,
    //       syncToXero: false,
    //       notes: "",
    //     },
    //   ]);
    //   return false;
    // }
    const nonBOAProducts = await apiGetNonBOAProductsList(keyword);
    if (nonBOAProducts) {
      // Insert generic NONBOA item
      const genericNonBOA = {
        id: null,
        barcode: "NONBOA",
        description: "",
        buyPrice: null,
        sellPrice: null,
        isDiscountApplied: false,
        syncToXero: false,
        notes: "",
        isNonBOA: true,
      };
      nonBOAProducts.unshift(genericNonBOA);

      setNonBOAProductList(nonBOAProducts);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      showErrorToast("Something went wrong");
    }
  };

  const handleResultSelect = (e, { result }) => {
    setSelectedNonBOAItemIndex(null);
    setFormData(result);
    setNonBOAProductSaved(false);
    setAddNonBOAItemOpen(true);
  };

  const handleSearchChange = (e, { value }) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  // Debounced search logic
  const debouncedSearch = useMemo(
    () =>
      _.debounce((value) => {
        setSearchTerm(value);
      }, 1000),
    []
  );

  const handleFocus = () => {
    setOpen(true); // Show all results on focus
  };

  const handleBlur = () => {
    // Delay closing to allow `onResultSelect` to trigger
    const timeout = setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  const NumericInputChecker = (event, decimalPlaces = 2) => {
    const { key, target } = event;
    const inputValue = target.value;

    // Check if there is already a decimal point
    const hasDecimal = inputValue.includes(".");
    const decimalIndex = inputValue.indexOf(".");
    const isAfterDecimal =
      decimalIndex !== -1 && target.selectionStart > decimalIndex;
    const digitsAfterDecimal = isAfterDecimal
      ? inputValue.slice(decimalIndex + 1).length
      : 0;

    // Allow control keys like Backspace, Delete, and Arrow keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];

    // Handle input conditions
    if (
      // Allow numeric keys (0-9) if they don't exceed the allowed decimal places
      (!isNaN(key) &&
        (!isAfterDecimal || digitsAfterDecimal < decimalPlaces)) ||
      // Allow essential control keys
      allowedKeys.includes(key) ||
      // Allow one decimal point
      (key === "." && !hasDecimal)
    ) {
      return; // Allow input if conditions are met
    }

    // Prevent all other keys
    event.preventDefault();
  };

  const onNewNonNOAProduct = () => {
    setNonBOAProductSaved(false);
    const newData = {
      id: 0,
      barcode: "",
      description: "",
      buyPrice: null,
      sellPrice: null,
      isDiscountApplied: false,
      syncToXero: false,
      notes: "",
      quantity: null,
    };
    setFormData(newData);
  };

  function handleChange(dataType, value) {
    let newState = [];
    newState.push(formData);
    let data = newState.map((item, i) => {
      if (dataType === "barcode") {
        setHasBarcodeError(false);
        value = value.slice(0, 30);
      }
      if (dataType === "description") {
        value = value.slice(0, 50);
      }
      if (i == 0) {
        return { ...item, [dataType]: value };
      }
      return item;
    });

    setFormData(data[0]);
  }

  const onSubmitAddNonBOAProduct = async () => {
    setIsLoading(true);
    setNonBOAProductSaved(true);
    if (!formData.barcode || formData.barcode === "") {
      showErrorToast("SKU field is required");
      setIsLoading(false);
      return false;
    }
    if (
      formData.isNonBOA &&
      (!formData.description || formData.description === "")
    ) {
      showErrorToast("Description field is required");
      setIsLoading(false);
      return false;
    }
    if (selectedNonBOAItemIndex !== null) {
      onUpdateNonBOAItem(formData, selectedNonBOAItemIndex);
      setAddNonBOAItemOpen(false);
      setNonBOAProductSaved(true);
      setSearchValue("");
      debouncedSearch("");
      setIsLoading(false);
      return false;
    }
    if (
      formData.id > 0 ||
      (formData.barcode === "NONBOA" && formData.isNonBOA)
    ) {
      onAddNonBOAItem(formData);
      setAddNonBOAItemOpen(false);
      setNonBOAProductSaved(true);
      setSearchValue("");
      debouncedSearch("");
      setIsLoading(false);
      return false;
    }
    try {
      const result = await apiAddNonBOAProduct(formData);
      if (result) {
        setAddNonBOAItemOpen(false);
        setNonBOAProductSaved(true);
        showSuccessToast(
          <div>
            <i className="check icon" /> Non-BOA Item Created and Added
            Successfully
          </div>
        );
        result.quantity = formData.quantity;
        onAddNonBOAItem(result);
        setSearchValue("");
        debouncedSearch("");
        setIsLoading(false);
      } else {
        setHasBarcodeError(true);
        setIsLoading(false);
      }
    } catch (e) {
      showErrorToast("Something went wrong");
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
  };

  const resultRenderer = (data, index) => {
    return (
      <div>
        <Grid style={{ color: "#808080" }}>
          <Grid.Column
            width={8}
            style={{ paddingLeft: 12, paddingRight: 0, paddingBottom: 0 }}
          >
            <Button
              style={{
                backgroundColor: "#fff",
                border: "1px solid #808080",
                padding: 3,
                fontSize: 13,
                marginTop: -3,
              }}
            >
              <span>{data.barcode}</span>
            </Button>
          </Grid.Column>
          {data.id ? (
            <>
              <Grid.Column
                width={4}
                style={{ paddingLeft: 0, paddingBottom: 0, paddingRight: 0 }}
              >
                {localStorage.getItem("boahubShowRrp") === "true" && (
                  <div
                    style={{
                      textAlign: "left",
                      display: "inline-block", // Ensure the span is inline-block
                      whiteSpace: "nowrap", // Prevent line breaks
                      overflow: "hidden", // Hide overflow content
                      textOverflow: "ellipsis", // Show ellipsis when content overflows
                      maxWidth: "100%",
                    }}
                  >
                    <span>SELL PRICE: {formatPrice(data.sellPrice)}</span>
                  </div>
                )}
              </Grid.Column>
              <Grid.Column
                width={4}
                style={{ paddingLeft: 0, paddingBottom: 0, paddingRight: 0 }}
              >
                {localStorage.getItem("boahubShowPurchasePrice") === "true" && (
                  <div
                    style={{
                      textAlign: "left",
                      display: "inline-block", // Ensure the span is inline-block
                      whiteSpace: "nowrap", // Prevent line breaks
                      overflow: "hidden", // Hide overflow content
                      textOverflow: "ellipsis", // Show ellipsis when content overflows
                      maxWidth: "100%",
                    }}
                  >
                    <span>BUY PRICE: {formatPrice(data.buyPrice)}</span>
                  </div>
                )}
              </Grid.Column>
              <Grid.Column
                width={16}
                style={{ paddingLeft: 13, paddingTop: 3 }}
              >
                <span style={{ fontSize: 12 }}>{data.description}</span>
              </Grid.Column>
            </>
          ) : (
            <Grid.Column width={8} style={{ paddingBottom: 33 }}></Grid.Column>
          )}
        </Grid>
      </div>
    );
  };

  return (
    <>
      <Modal
        className="non-boa-create-modal"
        size="tiny"
        open={addNonBOAItemOpen}
      >
        <Modal.Header>
          {formData?.nonBOAProductId || formData?.seqId >= 0
            ? "Edit Non-BOA Item"
            : formData?.id > 0 ||
              (formData.barcode === "NONBOA" && formData.isNonBOA)
            ? "Select Non-BOA Item"
            : "Create and Add Non-BOA Item"}
        </Modal.Header>
        <div style={{ position: "absolute", top: 200, left: 242 }}>
          {isLoading ? (
            <Loader
              active
              className="workaround"
              size="large"
              inline="centered"
            />
          ) : null}
        </div>
        <Modal.Content
          style={{
            opacity: isLoading ? 0.2 : 1,
            pointerEvents: isLoading ? "none" : "",
          }}
        >
          <Form size="tiny" style={{ fontSize: 14 }}>
            <Grid style={{ minHeight: 200 }} disabled>
              <div
                className="nonBOAProduct-form-content"
                style={{ paddingRight: "0px !important" }}
              >
                <Form size="tiny">
                  <Grid>
                    <Grid.Row style={{ paddingBottom: 0, paddingTop: 25 }}>
                      <Grid.Column width={16} style={{ paddingBottom: 15 }}>
                        <Form.Field
                          required
                          style={{
                            opacity:
                              formData?.id > 0 ||
                              formData?.nonBOAProductId > 0 ||
                              formData.barcode === "NONBOA"
                                ? 0.7
                                : 1,
                            pointerEvents:
                              formData?.id > 0 ||
                              formData?.nonBOAProductId > 0 ||
                              formData.barcode === "NONBOA"
                                ? "none"
                                : "",
                          }}
                        >
                          <label>SKU</label>
                          <Input
                            className={
                              (formData?.barcode === "" ||
                                formData?.barcode === null ||
                                !formData?.barcode) &&
                              nonBOAProductSaved
                                ? "error"
                                : hasBarcodeError
                                ? "error"
                                : ""
                            }
                            value={formData?.barcode}
                            onChange={(e, data) =>
                              handleChange("barcode", data.value)
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column width={16} style={{ paddingBottom: 15 }}>
                        <Form.Field
                          style={{
                            opacity:
                              formData?.barcode !== "NONBOA" &&
                              (formData?.id > 0 ||
                                formData?.nonBOAProductId > 0 ||
                                formData?.nonBOAProductId > 0 ||
                                formData?.seqId >= 0)
                                ? 0.7
                                : 1,
                            pointerEvents:
                              formData?.barcode !== "NONBOA" &&
                              (formData?.id > 0 ||
                                formData?.nonBOAProductId > 0 ||
                                formData?.nonBOAProductId > 0 ||
                                formData?.seqId >= 0)
                                ? "none"
                                : "",
                          }}
                          required={formData.isNonBOA}
                        >
                          <label>Description</label>
                          <Input
                            className={
                              formData?.isNonBOA &&
                              nonBOAProductSaved &&
                              (formData?.description === "" ||
                                formData?.description === null ||
                                !formData?.description)
                                ? "error"
                                : ""
                            }
                            value={formData?.description}
                            onChange={(e, data) =>
                              handleChange("description", data.value)
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column width={16}>
                        <Grid>
                          {localStorage.getItem("boahubShowRrp") === "true" && (
                            <Grid.Column width={8}>
                              <Form.Field>
                                <label>Sell Price</label>
                                {!dataLoading ? (
                                  <Input
                                    disabled={
                                      localStorage.getItem("userType") ===
                                        "4" && formData?.barcode !== "NONBOA"
                                    }
                                    defaultValue={formData?.sellPrice}
                                    onBlur={(e) =>
                                      handleChange("sellPrice", e.target.value)
                                    }
                                    onKeyDown={(event) =>
                                      NumericInputChecker(event, 2)
                                    }
                                  />
                                ) : (
                                  <Input disabled />
                                )}
                              </Form.Field>
                            </Grid.Column>
                          )}
                          {localStorage.getItem("boahubShowPurchasePrice") ===
                            "true" && (
                            <Grid.Column width={8}>
                              <Form.Field>
                                <label>Buy Price</label>
                                {!dataLoading ? (
                                  <Input
                                    disabled={
                                      localStorage.getItem("userType") === "4"
                                    }
                                    defaultValue={formData?.buyPrice}
                                    onBlur={(e) =>
                                      handleChange("buyPrice", e.target.value)
                                    }
                                    onKeyDown={(event) =>
                                      NumericInputChecker(event, 2)
                                    }
                                  />
                                ) : (
                                  <Input disabled />
                                )}
                              </Form.Field>
                            </Grid.Column>
                          )}
                        </Grid>
                      </Grid.Column>
                      <Grid.Column
                        width={16}
                        style={{ paddingTop: 15, paddingBottom: 10 }}
                      >
                        <Form.Field>
                          <label>Quantity</label>
                          {!dataLoading ? (
                            <Input
                              defaultValue={formData?.quantity}
                              onBlur={(e) =>
                                handleChange("quantity", e.target.value)
                              }
                              onKeyDown={(event) =>
                                NumericInputChecker(event, 1)
                              }
                            />
                          ) : (
                            <Input disabled />
                          )}
                        </Form.Field>
                      </Grid.Column>
                      {localStorage.getItem("userType") !== "4" ? (
                        <>
                          <Grid.Column width={8} style={{ paddingTop: 22 }}>
                            <Form.Field
                              style={{
                                opacity:
                                  formData?.id > 0 ||
                                  formData?.nonBOAProductId > 0
                                    ? 0.7
                                    : 1,
                                pointerEvents:
                                  formData?.id > 0 ||
                                  formData?.nonBOAProductId > 0
                                    ? "none"
                                    : "",
                              }}
                            >
                              <label>Apply Customer Discount</label>
                            </Form.Field>
                          </Grid.Column>
                          <Grid.Column
                            width={8}
                            style={{
                              paddingTop: 21,
                              textAlign: "right",
                              paddingLeft: 0,
                            }}
                          >
                            <Checkbox
                              disabled={
                                formData?.id > 0 ||
                                formData?.nonBOAProductId > 0
                              }
                              className="small-toggle"
                              style={{ marginTop: -1 }}
                              toggle
                              checked={formData?.isDiscountApplied}
                              onChange={(e, data) =>
                                handleChange("isDiscountApplied", data.checked)
                              }
                            />
                          </Grid.Column>
                          {localStorage.getItem("isXeroConnected") === "true" &&
                          !formData?.nonBOAProductId &&
                          !formData.id &&
                          !formData.isNonBOA ? (
                            <>
                              <Grid.Column width={10} style={{ paddingTop: 7 }}>
                                <Form.Field>
                                  <label>
                                    Sync Non-BOA item to{" "}
                                    <span style={{ color: "#00b6e3" }}>
                                      Xero
                                    </span>
                                  </label>
                                </Form.Field>
                              </Grid.Column>
                              <Grid.Column
                                width={6}
                                style={{
                                  paddingTop: 16,
                                  textAlign: "right",
                                  paddingLeft: 0,
                                }}
                              >
                                <style>
                                  {`
                                    /* Use a custom class to increase specificity */
                                    .small-toggle-xero.ui.toggle.checkbox input:checked ~ .box:before,
                                    .small-toggle-xero.ui.toggle.checkbox input:checked ~ label:before {
                                    background-color: #00b6e3 !important; /* Pink toggle */
                                    border-color: #00b6e3 !important;
                                    }
                                  `}
                                </style>
                                <Checkbox
                                  className="small-toggle-xero"
                                  style={{ marginTop: -11 }}
                                  toggle
                                  checked={formData.syncToXero}
                                  onChange={(e, data) =>
                                    handleChange("syncToXero", data.checked)
                                  }
                                />
                              </Grid.Column>
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </Grid.Row>
                  </Grid>
                </Form>
              </div>
            </Grid>
            <Grid style={{ marginTop: 50 }}>
              <Grid.Column width={8} style={{ paddingRight: 5 }}>
                <Button
                  size="tiny"
                  style={{
                    width: "100%",
                    marginLeft: "auto",
                  }}
                  onClick={() => {
                    setSearchValue("");
                    debouncedSearch("");
                    setAddNonBOAItemOpen(false);
                  }}
                >
                  DISCARD
                </Button>
              </Grid.Column>
              <Grid.Column width={8} style={{ paddingLeft: 5 }}>
                <Button
                  size="tiny"
                  style={{
                    width: "100%",
                    backgroundColor: "rgb(102, 204, 35)",
                    color: "#fff",
                    marginLeft: "auto",
                  }}
                  onClick={() => {
                    onSubmitAddNonBOAProduct();
                  }}
                >
                  {formData?.nonBOAProductId || formData?.seqId >= 0
                    ? "SAVE"
                    : formData?.id > 0 ||
                      (formData.barcode === "NONBOA" && formData.isNonBOA)
                    ? "SAVE"
                    : "CREATE"}
                </Button>
              </Grid.Column>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
      <Modal
        size="mini"
        open={openRemoveNonBOAProduct}
        onClose={() => setOpenRemoveNonBOAProduct(false)}
      >
        <div style={{ position: "absolute", bottom: 100, left: 150 }}>
          {isLoading ? (
            <Loader
              active
              className="workaround"
              size="large"
              inline="centered"
            />
          ) : null}
        </div>
        <Modal.Content
          style={{ opacity: isLoading ? 0.2 : 1, textAlign: "center" }}
        >
          <p>
            Are you sure <br /> you want to remove Non-BOA Item?
            <br />
            <br />
            {selectedNonBOAProduct.barcode}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenRemoveNonBOAProduct(false)}>NO</Button>
          <Button
            style={{
              backgroundColor: "rgb(102, 204, 35)",
              color: "#fff",
            }}
            onClick={() => {
              setOpenRemoveNonBOAProduct(false);
              onRemoveNonBOAItem(selectedNonBOAItemIndex);
            }}
          >
            YES
          </Button>
        </Modal.Actions>
      </Modal>
      <Grid>
        {!disabled && (
          <>
            <Grid.Column
              width={localStorage.getItem("userType") !== "4" ? 12 : 16}
              style={{
                paddingTop: 16,
                paddingRight:
                  localStorage.getItem("userType") !== "4" ? 5 : "1rem",
              }}
            >
              <Search
                className="non-boa-items-search-box"
                size="small"
                loading={isLoading}
                placeholder={loading ? "" : "Search for Non-BOA items"}
                onResultSelect={handleResultSelect}
                onSearchChange={handleSearchChange}
                noResultsMessage={
                  searchTerm && !isLoading
                    ? "No Non-BOA items found"
                    : "Search for Non-BOA items"
                }
                results={nonBOAProductList}
                onFocus={handleFocus}
                open={open}
                onBlur={handleBlur}
                resultRenderer={resultRenderer}
                onKeyDown={(event) =>
                  event.key === "Enter" && event.preventDefault()
                }
                value={seachValue}
              />
            </Grid.Column>
            {localStorage.getItem("userType") !== "4" ? (
              <Grid.Column width={4}>
                <Button
                  className="active-status"
                  size="small"
                  style={{
                    width: "100%",
                    fontSize: 14.5,
                    paddingLeft: 5,
                    paddingRight: 5,
                    paddingTop: 12,
                  }}
                  onClick={() => {
                    setSelectedNonBOAItemIndex(null);
                    onNewNonNOAProduct();
                    setAddNonBOAItemOpen(true);
                  }}
                >
                  <Icon name="add" style={{ color: "#fff" }} />
                  Create New
                </Button>
              </Grid.Column>
            ) : null}
          </>
        )}
        <Grid.Column
          width={16}
          style={{
            padding: "0px 17px",
            paddingTop: disabled ? 20 : 0,
          }}
        >
          {inventoryNonBOA?.map((data, index) => {
            return (
              <div key={index}>
                <Card
                  className="non-boa-items-card"
                  style={{
                    width: "100%",
                    minHeight: 70,
                    marginBottom: 12,
                    cursor: "pointer",
                  }}
                >
                  <Card.Content
                    onClick={() => {
                      setFormData(data);
                      setSelectedNonBOAItemIndex(index);
                      setAddNonBOAItemOpen(true);
                    }}
                  >
                    {data.isDiscountApplied &&
                    localStorage.getItem("userType") !== "4" ? (
                      <Icon
                        style={{
                          position: "absolute",
                          top: 1,
                          right: 2,
                          fontSize: 16,
                          color: "#66CC23",
                        }}
                        link
                        name="tags"
                      />
                    ) : null}
                    {!disabled && (
                      <Popup
                        position="top center"
                        trigger={
                          <Icon
                            name="trash"
                            style={{
                              fontSize: 18,
                              cursor: "pointer",
                              color: "red",
                              zIndex: 2,
                              position: "absolute",
                              top: 22,
                              right: 25,
                              marginRight: 0,
                            }}
                            onClick={(e) => {
                              handleDeleteClick(e);
                              setSelectedNonBOAProduct(data);
                              setSelectedNonBOAItemIndex(index);
                              setOpenRemoveNonBOAProduct(true);
                            }}
                          />
                        }
                        content="Remove"
                        style={{
                          fontSize: 12,
                          opacity: 0.7,
                        }}
                        inverted
                      />
                    )}
                    <Grid>
                      <Grid.Column
                        width={6}
                        style={{
                          paddingRight: 0,
                          paddingTop: 12,
                          paddingBottom: 0,
                        }}
                      >
                        <span style={{ fontWeight: "bold", fontSize: 16 }}>
                          {`${data.barcode ? data.barcode : "NONBOA"}`}
                        </span>
                      </Grid.Column>
                      <Grid.Column
                        width={3}
                        style={{ paddingRight: 0, paddingBottom: 0 }}
                      >
                        {localStorage.getItem("boahubShowRrp") === "true" && (
                          <span
                            style={{
                              fontWeight: "bold",
                              fontSize: 13,
                              color: "rgba(0, 0, 0, 0.69)",
                            }}
                          >
                            Sell Price: {formatPrice(data.sellPrice)}
                          </span>
                        )}
                      </Grid.Column>
                      <Grid.Column
                        width={3}
                        style={{ paddingRight: 0, paddingBottom: 0 }}
                      >
                        {localStorage.getItem("boahubShowPurchasePrice") ===
                          "true" && (
                          <span
                            style={{
                              fontWeight: "bold",
                              fontSize: 13,
                              color: "rgba(0, 0, 0, 0.69)",
                            }}
                          >
                            Buy Price: {formatPrice(data.buyPrice)}
                          </span>
                        )}
                      </Grid.Column>
                      <Grid.Column
                        width={4}
                        style={{ paddingRight: 0, paddingBottom: 0 }}
                      >
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: 13,
                            color: "rgba(0, 0, 0, 0.69)",
                          }}
                        >
                          Quantity: {`${data.quantity ? data.quantity : "1"}`}
                        </span>
                      </Grid.Column>
                      <Grid.Column width={16} style={{ paddingTop: 2 }}>
                        <span style={{ fontSize: 13, color: "#8C8C8C" }}>
                          {`${data.description ? data.description : ""}`}
                        </span>
                      </Grid.Column>
                    </Grid>
                  </Card.Content>
                </Card>
              </div>
            );
          })}
        </Grid.Column>
      </Grid>
    </>
  );
};

NonBOAItemsForm.propTypes = {
  value: PropTypes.number,
  onChangeHandler: PropTypes.func,
};

export default NonBOAItemsForm;
